/** What the last save's check of a booking-system API key learned — read from the item's
 *  `detail()`, never from the vendor (the backend probes on save and caches the answer).
 *
 *  Ventrata's provider config sends it as `detail.ventrata_key` (plan 2026-10-08 §3.8, typed by
 *  the spec as `ConsolePreflightVentrataKeyDetail`): the supplier the key belongs to (name,
 *  regional endpoint, country, contact), the languages it serves, whether it verified and why
 *  not, and every other tenant already holding the same key. The card renders any restech's
 *  probe of that shape; the next integration with one adds its typed key to `keyProbesOf`.
 *
 *  "Re-check key" verifies the STORED key again without retyping it: the console only ever sees
 *  the key's mask, and a configure body of nothing but that mask asks the backend to probe the
 *  stored key again (it writes nothing). The refreshed probe arrives with the item's refetch.
 *
 *  A shared key is a WARNING and nothing more (owner decision 2026-10-08: no confirmation
 *  step): two tenants on one key would both receive and import every booking it can see, so
 *  it is said first and loudly, naming the other tenant.
 */
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import {
  fieldErrorsOf,
  type PreflightActionField,
  type PreflightItem,
  type PreflightItemDetail,
  type PreflightVentrataKeyDetail,
  type PreflightVentrataSupplier,
} from '@/api/preflight';
import { HelpLink } from '@/components/HelpLink';
import { act, ValueActions } from '@/components/ValueActions/ValueActions';
import { formatDate, formatRelativeTime } from '@/utils/format';

import { copyOnlyActions, isMasked, urlActions } from '../tenantLinks';

/** `detail.ventrata_key` — see the backend's `RestechProviderConfig.detail()`. */
export type KeyProbe = PreflightVentrataKeyDetail;
export type KeyProbeSupplier = PreflightVentrataSupplier;
/** Another tenant (and account; "" = its default one) holding the same key. */
export type KeySharedWith = KeyProbe['shared_with'][number];

/** Every key probe an item's detail carries, with the restech it is about. */
export function keyProbesOf(detail: PreflightItemDetail): Array<{ restech: string; probe: KeyProbe }> {
  return detail.ventrata_key ? [{ restech: 'ventrata', probe: detail.ventrata_key }] : [];
}

/** The credential field a re-check submits (Ventrata's `recheckable_secrets()`). */
const RECHECK_FIELD = 'api_key';

/** The mask the API serves for the stored key — exactly what a re-check sends back — or null
 *  when no key is stored (an unset secret field is served without a mask). */
export function storedKeyMask(fields: readonly PreflightActionField[]): string | null {
  const f = fields.find((x) => x.key === RECHECK_FIELD && x.secret);
  return typeof f?.value === 'string' && isMasked(f.value) ? f.value : null;
}

const restechName = (code: string) => (code ? code.charAt(0).toUpperCase() + code.slice(1) : code);

function SharedKeyWarning({ restech, others }: { restech: string; others: KeySharedWith[] }) {
  return (
    <Alert
      severity="warning"
      variant="outlined"
      sx={{ mb: 1.5, borderWidth: 2 }}
      action={<HelpLink story="ventrata-onboarding" newTab />}
    >
      <AlertTitle sx={{ fontWeight: 700 }}>
        This {restechName(restech)} key is already used by {others.length === 1 ? 'another tenant' : `${others.length} other tenants`}
      </AlertTitle>
      <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2.5 }}>
        {others.map((t) => (
          <li key={`${t.tour_operator_id}:${t.account}`}>
            <Link component={RouterLink} to={`/tour-operators/${t.tour_operator_id}`}>
              {t.code}
            </Link>{' '}
            — {t.name}
            {t.account ? `, account ${t.account}` : ''}
          </li>
        ))}
      </Stack>
      <Typography variant="body2" sx={{ mt: 1 }}>
        Both tenants would receive and import every booking this key can see. Nothing blocks the
        setup — make sure this is intended (one supplier account behind two brands, say) before
        the booking feed is created.
      </Typography>
    </Alert>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box sx={{ display: 'contents' }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>
        {children}
      </Typography>
    </Box>
  );
}

function SupplierDetails({ probe }: { probe: KeyProbe }) {
  const s = probe.supplier ?? {};
  const contact = s.contact ?? {};
  const languages = probe.languages ?? [];
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 1.5, rowGap: 0.5 }}>
      <Row label="Supplier">
        <ValueActions actions={copyOnlyActions(s.id, 'Copy supplier id')}>
          <strong>{s.name || '—'}</strong>
        </ValueActions>
      </Row>
      {s.endpoint && (
        <Row label="Endpoint">
          <ValueActions actions={copyOnlyActions(s.endpoint, 'Copy endpoint')}>
            <code>{s.endpoint}</code>
          </ValueActions>
        </Row>
      )}
      {s.country && <Row label="Country">{s.country}</Row>}
      <Row label="Languages">
        {languages.length === 0 ? (
          'not reported'
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {languages.map((l) => (
              <Chip
                key={l}
                size="small"
                variant={l === probe.default_language ? 'filled' : 'outlined'}
                label={l === probe.default_language ? `${l} (default)` : l}
              />
            ))}
          </Box>
        )}
      </Row>
      {(contact.website || contact.email || contact.telephone || contact.address) && (
        <Row label="Contact">
          <Stack spacing={0.25}>
            {contact.website && (
              <ValueActions actions={urlActions(contact.website, 'Open website')}>{contact.website}</ValueActions>
            )}
            {contact.email && (
              <ValueActions actions={[act.mailto(contact.email), act.copy(contact.email, 'Copy email')]}>
                {contact.email}
              </ValueActions>
            )}
            {contact.telephone && <span>{contact.telephone}</span>}
            {contact.address && <span>{contact.address}</span>}
          </Stack>
        </Row>
      )}
    </Box>
  );
}

function RecheckButton({
  name,
  onRecheck,
  disabled,
}: {
  name: string;
  onRecheck: () => void | Promise<unknown>;
  disabled: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recheck = () => {
    setBusy(true);
    setError(null);
    void Promise.resolve(onRecheck())
      // The host's snackbar stands down for a per-field 400 (the field editor shows those), and
      // this one came from no editor: it is said here. Anything else the host reports.
      .catch((e: unknown) => {
        const fields = fieldErrorsOf(e);
        if (fields) setError(Object.values(fields).join(' '));
      })
      .finally(() => setBusy(false));
  };
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      <Tooltip title={`Ask ${name} about the stored key again — no need to retype it.`}>
        <span>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={busy ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
            disabled={disabled || busy}
            onClick={recheck}
          >
            {busy ? 'Re-checking…' : 'Re-check key'}
          </Button>
        </span>
      </Tooltip>
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
    </Box>
  );
}

export function RestechKeyProbe({
  restech,
  probe,
  onRecheck,
  recheckDisabled = false,
}: {
  restech: string;
  probe: KeyProbe;
  /** Re-check the stored key; omitted when no key is stored (or the viewer cannot manage). */
  onRecheck?: () => void | Promise<unknown>;
  recheckDisabled?: boolean;
}) {
  const name = restechName(restech);
  const recheck = onRecheck ? <RecheckButton name={name} onRecheck={onRecheck} disabled={recheckDisabled} /> : null;
  const checkedAt = probe.checked_ts ? (
    <Tooltip title={formatDate(probe.checked_ts)}>
      <span>checked {formatRelativeTime(probe.checked_ts)}</span>
    </Tooltip>
  ) : null;
  return (
    <Box sx={{ mb: 1.5 }}>
      {probe.shared_with.length > 0 && <SharedKeyWarning restech={restech} others={probe.shared_with} />}
      {!probe.checked ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            {name} key not checked yet — saving the API key checks it with {name} and shows the
            supplier it belongs to here.
          </Typography>
          {recheck}
        </Box>
      ) : probe.verified ? (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Chip size="small" color="success" label={`${name} key verified`} />
            <Typography variant="caption" color="text.secondary">
              {checkedAt}
            </Typography>
            {recheck && <Box sx={{ ml: 'auto' }}>{recheck}</Box>}
          </Box>
          <SupplierDetails probe={probe} />
        </Paper>
      ) : (
        <Alert severity="error" action={recheck}>
          <AlertTitle>{name} did not accept this key</AlertTitle>
          {probe.error || 'The key could not be verified.'} This item stays pending until a key
          verifies, so a setup run never carries it into the catalogue sync or the booking feed.
          {recheck
            ? ' Paste a new key and save, or re-check the stored one.'
            : ' Paste the key again and save to re-check it.'}
          {checkedAt && (
            <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
              {checkedAt}
            </Typography>
          )}
        </Alert>
      )}
    </Box>
  );
}

/** Every key probe in an item's detail, each with "Re-check key" while a key is stored and the
 *  viewer may configure the item; nothing for an item without one. */
export function RestechKeyProbes({
  item,
  canManage,
  pending,
  onConfigure,
}: {
  item: PreflightItem;
  canManage: boolean;
  /** A mutation of this item is in flight. */
  pending: boolean;
  onConfigure: (values: Record<string, unknown>) => void | Promise<unknown>;
}) {
  const probes = keyProbesOf(item.detail);
  if (probes.length === 0) return null;
  const mask = canManage ? storedKeyMask(item.config_fields) : null;
  return (
    <>
      {probes.map(({ restech, probe }) => (
        <RestechKeyProbe
          key={restech}
          restech={restech}
          probe={probe}
          onRecheck={mask ? () => onConfigure({ [RECHECK_FIELD]: mask }) : undefined}
          recheckDisabled={pending}
        />
      ))}
    </>
  );
}
